'use client'

import {
    Button as BaseComponent,
    type ButtonProps as BaseProps,
    useStyleConfig,
} from '@chakra-ui/react'
import type { SystemProps } from '@chakra-ui/system'
import { merge } from 'lodash'
import React from 'react'
import { Tooltip, type TooltipProps } from '../Tooltip'

type Omitted = 'spinner' | 'isRound' | 'aria-label'

export type IconButtonProps = Omit<BaseProps, Omitted> & {
    /**
     * @default 'sm'
     */
    size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg'
    /**
     * @default 'ghost'
     */
    variant?: 'ghost' | 'subtle-ghost' | 'solid' | 'outline'
    /**
     * @default 'primary'
     */
    colorScheme?: 'primary' | 'error' | 'success'
    tooltipProps: Omit<TooltipProps, 'children'>
    'aria-label'?: BaseProps['aria-label']
}

const useIconButtonAriaLabel = (ariaLabel, tooltipLabel, componentName) => {
    return React.useMemo(() => {
        if (ariaLabel) return ariaLabel

        if (typeof tooltipLabel === 'string') return tooltipLabel

        throw new Error(`🚨 ${componentName}: The type of "aria-label" must be a string.
            You are passing a "ReactNode" type into the tooltip "label" prop. If you need it to be a "ReactNode" so you must pass the "aria-label" prop as a separated prop.
            `)
    }, [tooltipLabel, ariaLabel, componentName])
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
    (props, ref) => {
        const {
            size,
            variant,
            colorScheme,
            sx: externalSx,
            tooltipProps,
            children,
            ...rest
        } = props

        const styles = useStyleConfig('IconButton', {
            variant,
            size,
            colorScheme,
        })
        const sx = merge(styles, externalSx) as SystemProps
        const ariaLabel = useIconButtonAriaLabel(
            props['aria-label'],
            tooltipProps.label,
            'IconButton',
        )

        const { key: _tooltipKey, ...restTooltipProps } = tooltipProps

        return (
            <Tooltip {...restTooltipProps}>
                <BaseComponent
                    ref={ref}
                    data-component="IconButton"
                    aria-label={ariaLabel}
                    {...rest}
                    sx={{
                        padding: 0,
                        ...sx,
                        _hover: { ...sx._hover, ...props._hover },
                        _active: { ...sx._active, ...props._active },
                        _focus: { ...sx._focus, ...props._focus },
                    }}
                >
                    {children}
                </BaseComponent>
            </Tooltip>
        )
    },
)
